import { createMarkup } from "lib/helpers";
import { getStore } from "store";

const MAX_WIDTH = "max_width=";

const relativeToAbsolute = (baseUrl: string, relativeUrl: string) => {
    const splitBase = baseUrl.split("/");
    const splitRelative = relativeUrl.split("/");
    splitBase.pop();
    // ignore the current file name (or no string)
    // (ignore if "base" is the current folder without having slash in trail)
    for (let i = 0; i < splitRelative.length; i++) {
        if (splitRelative[i] == ".") {
            continue;
        }
        if (splitRelative[i] == "..") {
            splitBase.pop();
        } else {
            splitBase.push(splitRelative[i]);
        }
    }
    return splitBase.join("/");
};

// Guess best way to fix relative URL.
// 1. When URL starts with `/`, leave it as is. It's absolute url and a valid url
// 2. When URL starts with `http/https`, leave it as is. It's absolute url and a valid url
// 3. When URL starts without 1 & 2, it's a relative URL. Prepend current url OR https
export const guessFixRelativeUrl = (url: string): string => {
    if (typeof url !== "string") {
        return "";
    }
    const shortUrl = isShortUrl(url);
    if (shortUrl) {
        return shortUrl[0];
    }
    if (url.indexOf("mailto:") >= 0) {
        return url.trim();
    }
    if (!/^(?:\/|https?:\/\/)/i.test(url) && url) {
        if (url.indexOf(".") >= 0) {
            url = `https://${url}`;
        } else {
            url = relativeToAbsolute(window.location.href, url);
        }
    }

    return url.trim();
};

export const replaceInternalURL = (html) => {
    return html.replace(/href="(.*?)"/gi, function (searchValue, matchedUrl) {
        const currentHostname = window.location.host;
        try {
            const { hostname, href, origin } = new URL(matchedUrl);
            if (currentHostname === hostname) {
                return `href="${href.replace(origin, "")}"`;
            }
        } catch (error) {
            // no-op
        }
        return searchValue;
    });
};

export const replaceSingleInternalURL = (url: string) => {
    const currentHostname = window.location.host;
    let newUrl = url;
    try {
        const { hostname, href, origin } = new URL(newUrl);
        if (currentHostname === hostname) {
            newUrl = href.replace(origin, "");
        }
    } catch (error) {
        // no-op
    }
    return newUrl;
};

export const replaceEmptyAnchorSpan = (html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = createMarkup(html, { ADD_ATTR: ["target", "allow"], ADD_TAGS: ["iframe"] });
    document.body.appendChild(tempDiv);
    const links = Array.from(tempDiv.getElementsByTagName("a"));
    links.forEach((link) => {
        const href = link.getAttribute("href");
        if (!href || href === "null") {
            const tempSpan = document.createElement("span");
            if (link.id) {
                tempSpan.id = link.id;
            }
            if (link.className) {
                tempSpan.className = link.className;
            }
            if (link.style.length) {
                tempSpan.style.cssText = link.style.cssText;
            }
            tempSpan.innerHTML = createMarkup(link.innerHTML, { ADD_ATTR: ["target"] });
            link.replaceWith(tempSpan);
        }
    });
    tempDiv.remove();
    return tempDiv.innerHTML;
};

export const isLocalUrl = (url) => {
    const fixedUrl = url ? guessFixRelativeUrl(url) : url;
    return !!(fixedUrl?.charAt(0) === "/" || fixedUrl?.includes(window.location.host));
};

export const urlOnClickMobileAppIntercepter = (container: HTMLElement) => {
    // onclick event for mobile team to intercept
    const links = Array.from(container.getElementsByTagName("a"));
    links.forEach((link) => {
        link.onclick = function () {
            const webkit = (window as any).webkit;
            if (webkit && webkit.messageHandlers && webkit.messageHandlers.onPageLinkHandler) {
                webkit.messageHandlers.onPageLinkHandler.postMessage({
                    link: link.href,
                });
                return false;
            }
            return true;
        };
    });
};

/** when sitesFixImageBlurrinessNu72818 is OFF */
export const getImageUrl = (imageUrl: string, width: number, pixelDensity?: number) => {
    const zoom = window.visualViewport.scale || 1;
    const isFeatureFlagEnabled = getStore().getState().ldFlags?.sitesFixImageBlurrinessNu72818;
    if (isFeatureFlagEnabled) {
        const scaledWidth = Math.ceil(width * (pixelDensity || 1) * zoom);
        return imageUrl.replace(/max_width=[0-9]+/i, `${MAX_WIDTH}${scaledWidth}`);
    } else {
        const baseDensity = window.devicePixelRatio;
        const densityMultipler = pixelDensity > baseDensity ? pixelDensity : 1.2;
        return imageUrl.replace(/max_width=[0-9]+/i, `${MAX_WIDTH}${Math.ceil(width * densityMultipler)}`);
    }
};

const generateDynamicImageDescriptor = (sizes: number[], imageUrl: string, maxWidth?: number) => {
    const [, widthFromUrl] = imageUrl.split(MAX_WIDTH);
    const parsedWidthFromUrl = parseInt(widthFromUrl);
    const width = parsedWidthFromUrl > maxWidth ? maxWidth : parsedWidthFromUrl;
    return sizes
        .map((size) => {
            return `${getImageUrl(imageUrl, width, size)} ${size}x`;
        })
        .join(",");
};

/**
 * Generates a responsive image descriptor string (for srcSet) using dynamic densities.
 * Ensures high sharpness by producing URLs with scaled `max_width` parameters.
 *
 * @param sizes - An array of density multipliers (e.g., [1, 2, 3]).
 * @param imageUrl - The base image URL containing a `max_width=` parameter.
 * @param maxWidth - Optional base width for image scaling. Falls back to `window.innerWidth` if not provided.
 * @returns A comma-separated string suitable for use in `srcSet`.
 */
const generateDynamicImageDescriptorNew = (sizes: number[], imageUrl: string, maxWidth?: number) => {
    // Fallback to window width if maxWidth is not provided
    const baseWidth = !maxWidth || isNaN(maxWidth) ? window.innerWidth : maxWidth;

    return sizes
        .map((density) => {
            return `${getImageUrl(imageUrl, baseWidth, density)} ${density}x`;
        })
        .join(",");
};

/**
 * Returns a responsive image descriptor string based on the feature flag.
 * Uses an enhanced descriptor generator when `sitesFixImageBlurrinessNu72818` is true for better sharpness.
 *
 * @param imageUrl - The image URL to transform.
 * @param maxWidth - (Optional)The base width to scale from.
 * @returns A srcSet-compatible descriptor string.
 */
export const getImageDescriptors = (imageUrl: string, maxWidth?: number) => {
    const pixelRatios = [1, 2, 3];
    const isFeatureFlagEnabled = getStore().getState().ldFlags?.sitesFixImageBlurrinessNu72818;
    if (isFeatureFlagEnabled) {
        if (typeof window !== "undefined" && !pixelRatios.includes(window.devicePixelRatio)) {
            pixelRatios.push(window.devicePixelRatio);
        }
        return generateDynamicImageDescriptorNew(pixelRatios, imageUrl, maxWidth);
    }
    return generateDynamicImageDescriptor(pixelRatios, imageUrl, maxWidth);
};

// returns match if it's a DTS brand shorturl (i.e. [gallery])
export const isShortUrl = (url: string) => url.match(/\[(.*?)\]/);
